import React, { useRef } from "react";
import styled from "styled-components";
import { IPlayerCardData, IRingData } from "../../../types/IPlayerDetail";
import YellowCardSvg from "../../../assets/event_icons/Yellow_card.png";
import RedCardSvg from "../../../assets/event_icons/red_card.png";
import {IPlayerAttributesData } from "../../../types/IPlayerAttributesData";
import { useEffect, useState } from "react";
import LabelText from "../../../ui/LabelText";
import IApiResponse from "../../../types/IApiResponse";
import { postData } from "../../../services/api";
import apiUrls from "../../../config/apiUrls";
import handleApiResponse from "../../../utils/handleApiResponse";
import LabelNumber from "../../../styled-components/LabelNumber";
import SeasonBreakdownBarChart from "./SeasonBreakdownBarChart";
import Pitch from "../../Pitch";
import { ChemistryPlayerPositions, FormationData, TeamChemistry } from "../../../types/IOption";
import { ModuleSpinner } from "../../../ui/LoadingSpinner/ModuleSpinner";
import { FullViewBtn, ISimilarChemistry } from "../../PlayerInfo";
import VisibleON from "../../../assets/icons/VisibleON.png";
import { FullModuleFitView } from "../../../pages/Chemistry/UIContent";
import ModuleFitLargePitch from "../../ModuleFitLargePitch";
import { useTheme } from "../../../contexts/ThemeContext";

const TableRow = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.playerDataBorder};
`;

const SmallPitchContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  border-radius: 8px;
  padding: 16px;
  border: 1px solid ${({ theme }) => theme.playerDataBorder};
  background-color: ${({ theme }) => theme.newSearchBar};
  width: 400px;
  & p {
    font-family: Aeonik;
    font-size: 20px;
    font-style: normal;
    font-weight: 700;
    line-height: 24px;
    margin-block-start: 0;
    margin-block-end: 0;
    color: ${({ theme }) => theme.playerColor};
  }
  & .smallModuleHeader {
    display: flex;
    justify-content: space-between;
    align-items: center;
    & p:nth-child(2) {
      font-family: "DM Mono";
      font-weight: 500;
      font-size: 14px;
      color: ${({ theme }) => theme.NumberColor};
      & span {
        color: ${({ theme }) => theme.playerColor};
        font-size: 16px;
      }
    }
  }
  & .spinner-overlay {
    z-index: 9;
  }
`;

interface IOverAllProps {
  playerData: IPlayerCardData | undefined
  overAllData: IPlayerAttributesData | undefined;
  playerId: number;
  seasonId: number;
  club?: number;
}

export const OverallComponent: React.FC<IOverAllProps> = ({ playerData, overAllData, playerId, club, seasonId }): JSX.Element => {
  const [overallData, setOverAllData] = useState<IRingData[]>([]);
  const [positionResults, setPositionResults] = useState<ChemistryPlayerPositions[]>([]);
  const [teamChemistry, setTeamChemistry] = useState<TeamChemistry[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isHover, setIsHover] = useState<boolean>(false);
  const [isFullViewOpen, setIsFullViewOpen] = useState<boolean>(false);
  const [systemFitScore, setSystemFitScore] = useState<number>(0);
  const prevSeasonId = useRef<number | null>(null);
  const { theme } = useTheme();

  useEffect(() => {
    if (overAllData) {
      const data = overAllData;
      setOverAllData([
        {
          name: "FINAL THIRD",
          percent: data.playerAttributesUniverseTheirPos.finalThird.avgScore,
          valueText: data.playerAttributesUniverseTheirPos.finalThird.avgScore,
          color: "#00D1A8",
        },
        {
          name: "BUILD UP PLAY",
          percent: data.playerAttributesUniverseTheirPos.buildUpPlay.avgScore,
          valueText: data.playerAttributesUniverseTheirPos.buildUpPlay.avgScore,
          color: "#FF7800",
        },
        {
          name: "DEFENCE",
          percent: data.playerAttributesUniverseTheirPos.defence.avgScore,
          valueText: data.playerAttributesUniverseTheirPos.defence.avgScore,
          color: "#FFC2C2",
        },
        {
          name: "PHYSICAL",
          percent: data.playerAttributesUniverseTheirPos.physical.avgScore,
          valueText: data.playerAttributesUniverseTheirPos.physical.avgScore,
          color: "#C9F0B5",
        },
      ]);
    }
  }, [overAllData]);

  useEffect(() => {
    setPositionResults([]);
  }, [playerId]);

  useEffect(() => {
    if (isFullViewOpen) {
      setIsFullViewOpen(false);
    }
    const fetchSystemFitData = async () => {
      setIsLoading(true);
      try {
        const ApiRequestData = {
          selectedPlayerId: 0,
          similarPlayerId: playerId,
          teamId: playerData?.loanClubId! > 0 ? playerData?.loanClubId : club,
        };
        const response: IApiResponse<ISimilarChemistry> = await postData(apiUrls.PlayerSystemFit, ApiRequestData);
        handleApiResponse(
          response,
          (candidatePlayerChemistry) => {
            setPositionResults(candidatePlayerChemistry.newPlayerChemistry.positionResults);
            setTeamChemistry(
              candidatePlayerChemistry.newPlayerChemistry.teamChemstriys.filter(
                (connection) => playerId === connection.player1 || playerId === connection.player2
              )
            );
            setSystemFitScore(candidatePlayerChemistry.oldSystemFit);
          },
          (error) => {
            console.error(error);
          }
        );
      } catch (error) {
        console.error("Error fetching player card data:", error);
      }
      setIsLoading(false);
    };

    if (positionResults.length === 0 && club! > 0 && seasonId > 0 && seasonId !== prevSeasonId.current && !isLoading) {
      fetchSystemFitData();
    }
  }, [club, playerId, seasonId]);

  return (
    <div className="common-page-css">
      <div className="overall-main overall-main-back">
        <div className="overall-left">
          <SeasonBreakdownBarChart data={overallData} />
        </div>
        <SmallPitchContainer>
          <div className="smallModuleHeader">
            <p>System fit</p>
            <p>
              AV. KEY PARTNERSHIP SCORE <span>{isLoading ? "--" : systemFitScore? systemFitScore : "--"}</span>
            </p>
          </div>
          <div style={{ position: "relative" }} onMouseOver={() => setIsHover(true)} onMouseLeave={() => setIsHover(false)}>
            <Pitch
              isPattern={false}
              positionResults={positionResults}
              teamChemistry={teamChemistry}
              currentView={"moduleChemistry"}
              modulefitPlayer={playerId}
            />
            {isLoading && <ModuleSpinner />}
            {isHover && (
              <FullViewBtn onClick={() => setIsFullViewOpen(true)}>
                <img src={VisibleON} alt="full-view-button" />
              </FullViewBtn>
            )}
          </div>
        </SmallPitchContainer>
      </div>
      <div className="stat-table-container">
        <div className="stat-table-column">
          <TableRow className="stat-table-row">
            <LabelText className="overall-text">Mins played</LabelText>
            <LabelNumber className="chart-content-number">{overAllData?.playerAttributesUniverseAllPos?.overall?.totalMinsPlayed}</LabelNumber>
          </TableRow>
          <TableRow className="stat-table-row">
            <LabelText className="overall-text">Total Goals • Penalties</LabelText>
            <LabelNumber className="chart-content-number fouls-committed">
              {overAllData?.playerAttributesUniverseAllPos?.overall?.totalGoals}
              <strong>.</strong>
              {overAllData?.playerAttributesUniverseAllPos?.overall?.totalPenalties}
            </LabelNumber>
          </TableRow>
          <TableRow className="stat-table-row">
            <LabelText className="overall-text">PRIMARY ASSISTS</LabelText>
            <LabelNumber className="chart-content-number"> {overAllData?.playerAttributesUniverseAllPos?.overall?.totalAssists}</LabelNumber>
          </TableRow>
          <TableRow className="stat-table-row">
            <LabelText className="overall-text">SECONDARY ASSISTS</LabelText>
            <LabelNumber className="chart-content-number">{overAllData?.playerAttributesUniverseAllPos?.overall?.totalSecondAssists}</LabelNumber>
          </TableRow>
          <TableRow className="stat-table-row">
            <LabelText className="overall-text">TERTIARY ASSISTS</LabelText>
            <LabelNumber className="chart-content-number">{overAllData?.playerAttributesUniverseAllPos?.overall?.totalTertiaryAssists}</LabelNumber>
          </TableRow>
        </div>
        <div className="stat-table-column">
          <TableRow className="stat-table-row">
            <LabelText className="overall-text">Attack Contribution</LabelText>
            <LabelNumber className="chart-content-number">{overAllData?.playerAttributesUniverseAllPos?.overall?.performanceValue}</LabelNumber>
          </TableRow>
          <TableRow className="stat-table-row">
            <LabelText className="overall-text">X GOALS</LabelText>
            <LabelNumber className="chart-content-number">{overAllData?.playerAttributesUniverseAllPos?.overall?.averageXGoals}</LabelNumber>
          </TableRow>
          <TableRow className="stat-table-row">
            <LabelText className="overall-text">X ASSISTS</LabelText>
            <LabelNumber className="chart-content-number">{overAllData?.playerAttributesUniverseAllPos?.overall?.averageXAssists}</LabelNumber>
          </TableRow>
          <TableRow className="stat-table-row">
            <LabelText className="overall-text">fouls suffered</LabelText>
            <LabelNumber className="chart-content-number">{overAllData?.playerAttributesUniverseAllPos?.overall?.foulsSuffered}</LabelNumber>
          </TableRow>
          <TableRow className="stat-table-row">
            <LabelText className="overall-text">Discipline</LabelText>
            <LabelNumber className="chart-content-number fouls-committed">
              {/* <strong>.</strong> */}
              <img src={YellowCardSvg} alt="YellowCardSvg" />
              <span className="stat-card-figure">{overAllData?.playerAttributesUniverseAllPos?.overall?.totalYellowCards}</span>
              &nbsp;&nbsp;
              <img src={RedCardSvg} alt="RedCardSvg" />
              <span className="stat-card-discipline">{overAllData?.playerAttributesUniverseAllPos?.overall?.totalRedCards}</span>
              &nbsp;&nbsp;
              <LabelText className="overall-text">Fouls</LabelText>
              &nbsp;
              {overAllData?.playerAttributesUniverseAllPos?.overall?.foulsCommitted}
            </LabelNumber>
          </TableRow>
        </div>
      </div>
      <FullModuleFitView header={"System fit"} theme={theme} isFullViewOpen={isFullViewOpen} setIsFullViewOpen={setIsFullViewOpen}>
        <ModuleFitLargePitch systemFitScore={systemFitScore} isShowClubImg={false} positionResults={positionResults} teamChemistry={teamChemistry} oldPlayerID={playerId} />
      </FullModuleFitView>
    </div>
  );
};
